import { useState, useEffect, useContext, createContext } from 'react';
import authService from '../services/authService';

// Create User Context
const UserContext = createContext();

// User Provider Component
export const UserProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initializeUser = () => {
      try {
        const token = authService.getToken();
        const userInfo = authService.getCurrentUser();
        
        if (token && userInfo) {
          setUser(userInfo);
        }
      } catch (error) {
        console.error('Error initializing user:', error);
      } finally {
        setLoading(false);
      }
    };

    initializeUser();

    // Listen for auth events
    const handleAuthLogin = (event) => {
      setUser(event.detail);
    };

    const handleAuthLogout = () => {
      setUser(null);
    };

    window.addEventListener('auth-login', handleAuthLogin);
    window.addEventListener('auth-logout', handleAuthLogout);

    return () => {
      window.removeEventListener('auth-login', handleAuthLogin);
      window.removeEventListener('auth-logout', handleAuthLogout);
    };
  }, []);

  // Function to check if user has a specific privilege
  const hasPrivilege = (privilegeName) => {
    if (!user || !user.group || !user.group.privileges) {
      return false;
    }

    return user.group.privileges.includes(privilegeName);
  };

  // Function to check if user has any of the provided privileges
  const hasAnyPrivilege = (privileges) => {
    if (!Array.isArray(privileges)) {
      return hasPrivilege(privileges);
    }

    return privileges.some(privilege => hasPrivilege(privilege));
  };

  // Function to check if user has all of the provided privileges
  const hasAllPrivileges = (privileges) => {
    if (!Array.isArray(privileges)) {
      return hasPrivilege(privileges);
    }

    return privileges.every(privilege => hasPrivilege(privilege));
  };

  // Function to update user data
  const updateUser = (userData) => {
    setUser(userData);
  };

  const value = {
    user,
    loading,
    hasPrivilege,
    hasAnyPrivilege,
    hasAllPrivileges,
    updateUser
  };

  return (
    <UserContext.Provider value={value}>
      {children}
    </UserContext.Provider>
  );
};

// Custom hook to use user context
export const useUser = () => {
  const context = useContext(UserContext);
  
  if (!context) {
    throw new Error('useUser must be used within a UserProvider');
  }
  
  return context;
};